// src/components/admin/FiltrosUbicacion.js
//
// Selectores compactos para filtrar por Departamento / Municipio / Guia.
// Se renderiza como fragmentos de <TextField> para que el padre los meta
// en su propio Stack/Grid horizontal junto a los otros filtros.
//
// Uso:
//   const geo = useFiltrosUbicacion();  // hook que carga catálogos
//   ...
//   <SelectorDepartamento geo={geo} valor={f.departamentoId} onChange={...} />
//   <SelectorMunicipio    geo={geo} valor={f.municipioId}    onChange={...}
//                         departamentoId={f.departamentoId} />
//   <SelectorGuia         geo={geo} valor={f.guiaId}         onChange={...} />
//
// Los 3 selectores son TextField select tamaño small con ancho fijo
// adecuado al contenido (200/200/220 px respectivamente).

import React, { useEffect, useState } from 'react';
import { TextField, MenuItem } from '@mui/material';
import { api } from '../../api/apiClient';

/** Hook que carga departamentos y guias una sola vez. */
export function useFiltrosUbicacion({ cargarGuias = true } = {}) {
  const [departamentos, setDepartamentos] = useState([]);
  const [guias, setGuias] = useState([]);
  const [municipiosPorDepto, setMunicipiosPorDepto] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const [d, g] = await Promise.all([
          api.get('/api/pos/departamentos'),
          cargarGuias
            ? api.get('/api/pos/admin-plataforma/guias', { query: { estado: 'ACTIVO' } })
            : Promise.resolve([]),
        ]);
        setDepartamentos(d || []);
        setGuias(g || []);
      } catch (e) { /* silencioso, filtros opcionales */ }
    })();
  }, [cargarGuias]);

  const cargarMunicipios = async (departamentoId) => {
    if (!departamentoId || municipiosPorDepto[departamentoId]) return;
    try {
      const m = await api.get('/api/pos/municipios', { query: { departamentoId } });
      setMunicipiosPorDepto(prev => ({ ...prev, [departamentoId]: m || [] }));
    } catch { /* silencioso */ }
  };

  return { departamentos, guias, municipiosPorDepto, cargarMunicipios };
}

export function SelectorDepartamento({ geo, valor, onChange, sx }) {
  return (
    <TextField
      select size="small" label="Departamento"
      sx={{ width: 200, ...sx }}
      value={valor || ''}
      onChange={e => onChange(e.target.value)}
    >
      <MenuItem value="">Todos</MenuItem>
      {geo.departamentos.map(d => (
        <MenuItem key={d.departamentoID} value={d.departamentoID}>{d.nombre}</MenuItem>
      ))}
    </TextField>
  );
}

export function SelectorMunicipio({ geo, valor, onChange, departamentoId, sx }) {
  useEffect(() => {
    if (departamentoId) geo.cargarMunicipios(departamentoId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departamentoId]);

  const municipios = departamentoId ? (geo.municipiosPorDepto[departamentoId] || []) : [];
  return (
    <TextField
      select size="small" label="Municipio"
      sx={{ width: 200, ...sx }}
      value={valor || ''}
      onChange={e => onChange(e.target.value)}
      disabled={!departamentoId}
    >
      <MenuItem value="">Todos</MenuItem>
      {municipios.map(m => (
        <MenuItem key={m.municipioID} value={m.municipioID}>{m.nombre}</MenuItem>
      ))}
    </TextField>
  );
}

export function SelectorGuia({ geo, valor, onChange, sx }) {
  return (
    <TextField
      select size="small" label="Guía"
      sx={{ width: 220, ...sx }}
      value={valor || ''}
      onChange={e => onChange(e.target.value)}
    >
      <MenuItem value="">Todos</MenuItem>
      {geo.guias.map(g => (
        <MenuItem key={g.guiaId} value={g.guiaId}>{g.nombreCompleto}</MenuItem>
      ))}
    </TextField>
  );
}
